import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { keys } from '../lib/query-client.js';
import {
  effectivePlatform,
  toAccount,
  type Account,
  type AccountGroup,
  type AccountWire,
} from './types.js';
import type { Platform } from '@dintiko/shared';

/**
 * Which platforms this server can actually connect (PLAN §19).
 *
 * A platform whose developer app does not exist yet has no credentials, so the
 * API never registers it and it comes back `connectable: false`. The screen
 * hides its Connect button rather than offering one that would 404 — a button
 * that always fails is worse than no button.
 */
export function usePlatforms(): UseQueryResult<PlatformAvailability[]> {
  return useQuery({
    queryKey: keys.platforms,
    queryFn: () =>
      api.get<{ platforms: PlatformAvailability[] }>('/platforms').then((r) => r.platforms),
    // Only changes when the owner adds credentials and the API restarts.
    staleTime: 10 * 60_000,
  });
}

export interface PlatformAvailability {
  platform: Platform;
  connectable: boolean;
  /**
   * How this platform is connected.
   *
   * `oauth` leaves for a consent screen and comes back. `direct` has no
   * consent screen at all — Telegram's credential is a bot token the server
   * already holds, so the owner names a channel instead (D-035). `null` means
   * the platform is not set up on this server.
   */
  connectVia: 'oauth' | 'direct' | null;
}

/**
 * Connect a Telegram channel by name.
 *
 * A POST rather than a navigation, because nothing has to leave the page:
 * there is no consent screen to visit. The server checks the bot can actually
 * post to the channel before saving anything, so a rejection here is a real
 * answer about rights rather than a guess.
 */
export function useAddTelegramChannel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (channel: string) =>
      api.post<{ handle: string; added: boolean }>('/telegram/channels', { channel }),
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.accounts });
    },
  });
}

/**
 * Where the browser goes to start a grant.
 *
 * A full navigation, not a fetch: the owner has to leave for the platform's
 * consent screen and be sent back. Fetching this would follow the redirect
 * with XHR and land the consent page in a response body nobody can see.
 */
export function connectUrl(platform: Platform): string {
  return `/api/oauth/${platform}/start`;
}

export function useAccounts(): UseQueryResult<Account[]> {
  return useQuery({
    queryKey: keys.accounts,
    queryFn: () =>
      api.get<{ accounts: AccountWire[] }>('/accounts').then((r) => r.accounts.map(toAccount)),
    // Connections rarely change, and the composer reads this on every open.
    staleTime: 5 * 60_000,
  });
}

/**
 * Accounts grouped by the platform whose rules apply, in a stable order, so
 * the composer's chips and tabs never reshuffle between renders.
 */
/**
 * The preferred reading order. Anything outside it still appears — see below.
 */
const PREFERRED_ORDER: Platform[] = ['youtube', 'instagram', 'tiktok', 'facebook', 'linkedin'];

export function groupByPlatform(accounts: readonly Account[]): [Platform, Account[]][] {
  const groups = new Map<Platform, Account[]>();

  for (const account of accounts) {
    const platform = effectivePlatform(account);
    const existing = groups.get(platform);
    if (existing) existing.push(account);
    else groups.set(platform, [account]);
  }

  // Ordered platforms first, then anything the list has not heard of, rather
  // than dropping it. The old version ended in `order.filter(...)`, which
  // silently discarded every group not named in a hardcoded array — so the
  // moment Telegram joined the Platform union its connected accounts vanished
  // from the Accounts screen and from the composer's chips, while the header
  // went on counting them from the unfiltered list. A platform added to the
  // shared enum now shows up at the end instead of disappearing.
  const seen = new Set(PREFERRED_ORDER);
  const rest = [...groups.keys()].filter((platform) => !seen.has(platform)).sort();

  return [...PREFERRED_ORDER, ...rest]
    .filter((platform) => groups.has(platform))
    .map((platform) => [platform, (groups.get(platform) ?? []).sort(byHandle)]);
}

function byHandle(a: Account, b: Account): number {
  return a.handle.localeCompare(b.handle);
}

export function useUpdateAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      isEnabled?: boolean;
      defaultSettings?: Record<string, unknown>;
    }) => {
      const { id, ...body } = input;
      // The route answers { account: … }, like every other account endpoint.
      // Reading the envelope as the account itself left toAccount dereferencing
      // an undefined `connection`, which threw *after* the server had already
      // saved — so enabling an account worked and still said it had failed.
      return api
        .patch<{ account: AccountWire }>(`/accounts/${id}`, body)
        .then((response) => toAccount(response.account));
    },
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.accounts });
    },
  });
}

export function useDisconnectAccount() {
  const client = useQueryClient();
  return useMutation({
    // The answer matters: disconnecting removes an account that was never
    // posted to, and keeps one that was, because its posts point at it
    // (PLAN §14). Those are different outcomes and the owner is watching the
    // card to find out which they got.
    mutationFn: (id: string) => api.delete<{ accountRemoved: boolean }>(`/accounts/${id}`),
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.accounts });
    },
  });
}

export function useAccountGroups(): UseQueryResult<AccountGroup[]> {
  return useQuery({
    queryKey: keys.accountGroups,
    queryFn: () => api.get<{ groups: AccountGroup[] }>('/account-groups').then((r) => r.groups),
    staleTime: 5 * 60_000,
  });
}

export function useSaveAccountGroup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: string; name: string; accountIds: string[] }) =>
      input.id
        ? api
            .patch<{ group: AccountGroup }>(`/account-groups/${input.id}`, {
              name: input.name,
              accountIds: input.accountIds,
            })
            .then((response) => response.group)
        : api.post<{ group: AccountGroup }>('/account-groups', input).then((r) => r.group),
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.accountGroups });
    },
  });
}

export function useDeleteAccountGroup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/account-groups/${id}`),
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.accountGroups });
    },
  });
}

/**
 * TikTok's composer needs live creator_info before it can show the privacy
 * selector or decide which interaction toggles are allowed (PLAN §9.3).
 */
export interface CreatorInfo {
  nickname: string;
  avatarUrl: string | null;
  privacyLevelOptions: string[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoPostDurationSec: number;
}

export function useCreatorInfo(accountId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['creator-info', accountId ?? ''],
    enabled: Boolean(accountId) && enabled,
    // Must be fresh at compose time: TikTok requires the settings shown to
    // reflect the creator's current account state, not a cached snapshot.
    staleTime: 0,
    gcTime: 60_000,
    queryFn: () => api.get<CreatorInfo>(`/accounts/${String(accountId)}/tiktok/creator-info`),
  });
}
